import { memo, useState } from "react";
import { Filter, X, ArrowUpDown, Search, Package, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { AuctionCategory } from "@/types/auction";
import { sortOptions, type SortOption } from "@/components/SortSelect";

type SearchMode = "item" | "player";

interface MobileFilterFABProps {
  categories: AuctionCategory[];
  selectedCategory: string | null;
  onSelectCategory: (category: string | null) => void;
  sortOption: SortOption;
  onSortChange: (option: SortOption) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  activeFiltersCount: number;
}

const MobileFilterFAB = memo(({
  categories,
  selectedCategory,
  onSelectCategory,
  sortOption,
  onSortChange,
  searchQuery,
  onSearchChange,
  searchMode,
  onSearchModeChange,
  activeFiltersCount
}: MobileFilterFABProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'filter' | 'sort' | 'search'>('filter');

  const handleCategorySelect = (category: string | null) => {
    onSelectCategory(category);
  };

  const handleSortSelect = (option: SortOption) => {
    onSortChange(option);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* FAB Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full shadow-lg transition-all duration-300",
          "flex items-center justify-center",
          isOpen 
            ? "bg-destructive text-destructive-foreground rotate-0" 
            : "bg-primary text-primary-foreground"
        )}
        aria-label={isOpen ? "Filter schließen" : "Filter öffnen"}
      >
        {isOpen ? <X size={24} /> : <Filter size={24} />}
        {!isOpen && activeFiltersCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-chart-5 text-white text-xs flex items-center justify-center font-bold">
            {activeFiltersCount}
          </span>
        )}
      </button>

      {/* Filter Panel */}
      <div className={cn(
        "fixed bottom-36 right-4 left-4 z-50 transition-all duration-300 origin-bottom-right",
        isOpen 
          ? "opacity-100 scale-100 translate-y-0" 
          : "opacity-0 scale-95 translate-y-4 pointer-events-none"
      )}>
        <div className="bg-card border border-border rounded-2xl shadow-xl overflow-hidden max-h-[60vh] flex flex-col">
          {/* Tab Header */}
          <div className="flex border-b border-border">
            <button
              onClick={() => setActiveTab('filter')}
              className={cn(
                "flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors",
                activeTab === 'filter' 
                  ? "bg-primary/10 text-primary border-b-2 border-primary" 
                  : "text-muted-foreground"
              )}
            >
              <Filter size={16} />
              Kategorie
            </button>
            <button
              onClick={() => setActiveTab('sort')}
              className={cn(
                "flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors",
                activeTab === 'sort' 
                  ? "bg-primary/10 text-primary border-b-2 border-primary" 
                  : "text-muted-foreground"
              )}
            >
              <ArrowUpDown size={16} />
              Sortieren
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={cn(
                "flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors",
                activeTab === 'search' 
                  ? "bg-primary/10 text-primary border-b-2 border-primary" 
                  : "text-muted-foreground"
              )}
            >
              <Search size={16} />
              Suche
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-4 overflow-y-auto flex-1">
            {activeTab === 'filter' && (
              <div className="space-y-2">
                <button
                  onClick={() => handleCategorySelect(null)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-xl transition-colors",
                    selectedCategory === null 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-secondary/30 active:bg-secondary/50"
                  )}
                >
                  <span className="font-medium">Alle Kategorien</span>
                </button>
                {categories.map((category) => (
                  <button
                    key={category.name}
                    onClick={() => handleCategorySelect(category.name)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-xl transition-colors",
                      selectedCategory === category.name 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-secondary/30 active:bg-secondary/50"
                    )}
                  >
                    <img 
                      src={category.icon} 
                      alt="" 
                      className="w-6 h-6"
                      style={{ imageRendering: 'pixelated' }}
                    />
                    <span className="font-medium">{category.displayName}</span>
                  </button>
                ))}
              </div>
            )}

            {activeTab === 'sort' && (
              <div className="space-y-2">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleSortSelect(option.value)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-xl transition-colors",
                      sortOption === option.value 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-secondary/30 active:bg-secondary/50"
                    )}
                  >
                    <option.icon size={18} className={sortOption === option.value ? "text-primary-foreground" : "text-muted-foreground"} />
                    <span className="font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            )}

            {activeTab === 'search' && (
              <div className="space-y-4">
                {/* Search Mode Toggle */}
                <div className="flex rounded-lg border border-border/50 bg-secondary/30 p-1">
                  <button
                    onClick={() => onSearchModeChange("item")}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
                      searchMode === "item" 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground"
                    )}
                  >
                    <Package size={16} />
                    Items
                  </button>
                  <button
                    onClick={() => onSearchModeChange("player")}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
                      searchMode === "player" 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground"
                    )}
                  >
                    <User size={16} />
                    Spieler
                  </button>
                </div>

                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder={searchMode === "item" ? "Item suchen..." : "Spieler suchen..."}
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="pl-10 h-12 bg-secondary/30 border-border/50 text-base"
                    maxLength={100}
                    autoFocus
                  />
                </div>

                {searchQuery && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onSearchChange("")}
                    className="w-full"
                  >
                    Suche zurücksetzen
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

MobileFilterFAB.displayName = 'MobileFilterFAB';

export default MobileFilterFAB;
